/**
 * FiscalContextPdvDesktopService
 *
 * Gerencia o contexto fiscal local do PDV Desktop (Electron).
 *
 * Responsabilidades:
 *  1. Sincronizar dados fiscais via GET /api/offline/fiscal-context.
 *  2. Persistir emitente em emitente_local.
 *  3. Persistir configuração NFC-e em configuracao_nfce_local.
 *  4. Validar se o contexto fiscal está completo antes de emitir NFC-e.
 *
 * Campos obrigatórios para emissão:
 *  Emitente:  cnpj, ie, crt, codigo_municipio, municipio, uf
 *  Config:    serie, ambiente, idTokenCSC, tokenCSC
 */
import { getElectronAPI } from "@/plugins/desktop";
import { SYSTEM_VARIABLE } from "@/modules/Application";
import TerminalConfigService from "./TerminalConfigService";

function _getBaseUrl() {
  return (SYSTEM_VARIABLE.BASE_URL_API || "").replace(/\/$/, "");
}

const CAMPOS_EMITENTE = [
  { campo: "cnpj",             label: "CNPJ do emitente" },
  { campo: "ie",               label: "Inscrição Estadual" },
  { campo: "crt",              label: "CRT (Código Regime Tributário)" },
  { campo: "codigo_municipio", label: "Código IBGE do município" },
  { campo: "municipio",        label: "Município" },
  { campo: "uf",               label: "UF" },
];

const _valorPreenchido = (valor) =>
  valor !== null && valor !== undefined && String(valor).trim() !== "";

const _mensagemCertificadoAusente = () =>
  "Certificado digital não configurado.";

export function possuiCertificadoDigitalSincronizado(emitente = null) {
  const certificadoCaminho =
    emitente?.certificado_caminho ?? emitente?.certificado_nome;
  return (
    !!emitente?.certificado_configurado && _valorPreenchido(certificadoCaminho)
  );
}

export function avaliarDisponibilidadeNfce(
  { emitente = null, configuracao = null } = {},
  agora = new Date()
) {
  if (!configuracao || !configuracao.id_configuracao) {
    return {
      disponivel: false,
      codigo: "CONFIGURACAO_NFCE_AUSENTE",
      mensagem: TerminalConfigService.mensagemConfiguracaoNfceAusente(),
      problemas: [TerminalConfigService.mensagemConfiguracaoNfceAusente()],
    };
  }

  const problemas = [];
  if (Number(configuracao.modelo) !== 65)
    problemas.push("A configuração fiscal deve utilizar o modelo 65.");
  if (Number(configuracao.serie) <= 0)
    problemas.push("Série NFC-e não configurada.");
  if (![1, 2].includes(Number(configuracao.ambiente)))
    problemas.push("Ambiente NFC-e inválido.");
  if (Number(configuracao.proximaNota ?? 0) <= 0)
    problemas.push("Numeração NFC-e não configurada.");

  const idTokenCSC = configuracao.idTokenCSC ?? configuracao.id_token_csc;
  const tokenCSC = configuracao.tokenCSC ?? configuracao.token_csc;
  const semCsc = !_valorPreenchido(idTokenCSC) || !_valorPreenchido(tokenCSC);
  if (semCsc) problemas.push("CSC ou ID Token CSC não configurado.");

  if (!emitente) {
    problemas.push("Dados do emitente não encontrados.");
  } else {
    for (const { campo,      label } of CAMPOS_EMITENTE) {
      if (!_valorPreenchido(emitente[campo]))
        problemas.push(`${label} não configurado no emitente.`);
    }
  }

  const semCertificado = !possuiCertificadoDigitalSincronizado(emitente);
  let certificadoExpirado = false;
  if (!semCertificado && _valorPreenchido(emitente?.certificado_validade)) {
    const validade = new Date(`${emitente.certificado_validade}T23:59:59`);
    certificadoExpirado =
      Number.isNaN(validade.getTime()) || validade.getTime() < agora.getTime();
  }
  if (semCertificado) problemas.push(_mensagemCertificadoAusente());
  else if (!_valorPreenchido(emitente?.certificado_validade))
    problemas.push("Certificado digital indisponível ou inválido.");
  else if (certificadoExpirado) problemas.push("Certificado digital expirado.");

  let codigo = null;
  let mensagem = "NFC-e disponível.";
  if (problemas.length > 0) {
    if (semCsc) {
      codigo = "CSC_AUSENTE";
      mensagem =
        "CSC ou ID Token CSC não configurado. A emissão de Pré-venda continua disponível.";
    } else if (semCertificado) {
      codigo = "CERTIFICADO_DIGITAL_AUSENTE";
      mensagem = _mensagemCertificadoAusente();
    } else if (certificadoExpirado) {
      codigo = "CERTIFICADO_DIGITAL_EXPIRADO";
      mensagem =
        "Certificado digital expirado. A emissão de Pré-venda continua disponível.";
    } else {
      codigo = "CONTEXTO_FISCAL_INCOMPLETO";
      mensagem = problemas[0];
    }
  }

  return { disponivel: problemas.length === 0, codigo, mensagem, problemas };
}

const FiscalContextPdvDesktopService = {
  /**
   * Busca dados fiscais do backend e persiste localmente.
   * Endpoint: GET /api/offline/fiscal-context/{uuid_terminal}
   * Resposta esperada: { emitente: {}, configuracao_nfce: {}, terminal?: {} }
   */
  async sincronizarContextoFiscal() {
    const api = getElectronAPI();
    if (!api) throw new Error("Electron API não disponível.");
    try {
      await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    } catch (e) {
      if (TerminalConfigService.isCodigoSessaoEmpresaDivergente(e?.code)) {
        const err = new Error(
          "Não foi possível sincronizar a configuração fiscal.\n\n" +
            (e.message || "Terminal não pertence à empresa autenticada.")
        );
        err.code = e.code;
        throw err;
      }
      throw e;
    }

    const [apiToken, uuidTerminal] = await Promise.all([
      api.config.get("api_token"),
      api.config.get("uuid_terminal"),
    ]);
    if (!apiToken) throw new Error("Token de autenticação não encontrado. Faça login novamente.");
    if (!uuidTerminal) throw new Error("UUID do terminal não encontrado. Configure o terminal antes de sincronizar.");

    const baseUrl = _getBaseUrl();
    const resp = await fetch(`${baseUrl}/api/offline/fiscal-context?uuid_terminal=${uuidTerminal}`, {
      headers: {
        Authorization: `Bearer ${apiToken}`,
        Accept:        "application/json",
      },
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => resp.statusText);
      let erroPayload = {};
      try {
        erroPayload = JSON.parse(text);
      } catch (_) {
        /* resposta não JSON */
      }
      const code = erroPayload.code ?? erroPayload.error_code ?? null;
      if (TerminalConfigService.isCodigoConfiguracaoNfceAusente(code)) {
        await TerminalConfigService.limparConfiguracaoNfceTerminal();
        const disponibilidade = avaliarDisponibilidadeNfce();
        return { emitente: null, configuracao_nfce: null, ...disponibilidade };
      }
      const mensagemServidor =
        erroPayload.msg ?? erroPayload.message ?? erroPayload.error ?? null;
      const detalhe =
        mensagemServidor || resp.statusText || `HTTP ${resp.status}`;
      const err = new Error(
        `Não foi possível sincronizar a configuração fiscal.\n\n${detalhe}`
      );
      err.code = code;
      throw err;
    }

    const json = await resp.json();
    const dados = json.data ?? json;
    const emitente = dados.emitente ?? null;
    const configuracao_nfce =
      dados.configuracao_nfce ??
      dados.nfce_config ??
      dados.configuracaoNfce ??
      null;

    if (emitente) {
      // Garante id_empresa (coluna NOT NULL em emitente_local). O payload do back
      // pode não trazer id_empresa, ou trazê-lo com outra chave — então tentamos
      // várias origens e, por fim, a config local do terminal.
      if (emitente.id_empresa == null) {
        const idEmpresaCfg = await api.config.get("id_empresa");
        emitente.id_empresa =
          emitente.id ??
          emitente.empresa_id ??
          dados.id_empresa ??
          dados.empresa?.id_empresa ??
          dados.empresa?.id ??
          (idEmpresaCfg != null ? parseInt(idEmpresaCfg, 10) : null);
      }

      if (emitente.id_empresa == null) {
        throw new Error(
          "Não foi possível determinar o id_empresa do emitente no contexto fiscal. " +
          "Verifique se o endpoint /api/offline/fiscal-context retorna emitente.id_empresa."
        );
      }

      await api.emitente.salvarEmitente(emitente);
    }

    if (configuracao_nfce) {
      await TerminalConfigService.salvarConfiguracaoNfceTerminal({
        ...configuracao_nfce,
        payload_json: dados,
      });
    } else {
      await TerminalConfigService.limparConfiguracaoNfceTerminal();
    }

    // Descarta o certificado baixado anteriormente para forçar novo download
    // com o arquivo/senha recém-sincronizados (o certificado pode ter sido
    // trocado no painel web ou o cache local estar corrompido/desatualizado).
    try {
      if (api.certificado && api.certificado.limparCache) {
        await api.certificado.limparCache();
      }
    } catch (e) {
      console.warn("[FiscalContext] não foi possível limpar cache do certificado:", e?.message ?? e);
    }

    // Atualiza o controle de sincronização
    await api.sync.atualizarControle("fiscal_context", new Date().toISOString());

    const disponibilidade = avaliarDisponibilidadeNfce({
      emitente,
      configuracao: configuracao_nfce,
    });
    return { emitente, configuracao_nfce, ...disponibilidade };
  },

  /**
   * Retorna o contexto fiscal armazenado localmente.
   * @returns {{ emitente: object|null, configuracao: object|null }}
   */
  async buscarContextoFiscalLocal() {
    const api = getElectronAPI();
    if (!api) throw new Error("Electron API não disponível.");

    const [emitente, configuracao] = await Promise.all([
      api.emitente.buscarEmitente(),
      api.configuracaoNfce.buscarConfiguracao(),
    ]);

    return { emitente, configuracao };
  },

  /**
   * Valida se todos os campos necessários para emissão estão preenchidos.
   * @returns {{ valido: boolean, problemas: string[], emitente: object|null, configuracao: object|null }}
   */
  async validarContextoFiscalLocal() {
    const { emitente, configuracao } = await this.buscarContextoFiscalLocal();
    const disponibilidade = avaliarDisponibilidadeNfce({
      emitente,
      configuracao,
    });
    return {
        valido: disponibilidade.disponivel,
      ...disponibilidade,
      emitente,
      configuracao,
    };
  },
};

export default FiscalContextPdvDesktopService;
